var React = require('react');
var { useRef, useState, useEffect } = React;

function useConnectionManager(opts) {
  var canvasRef = opts.canvasRef;
  var panPosRef = opts.panPosRef;
  var zoomRef = opts.zoomRef;
  var setWorkflowState = opts.setWorkflowState;
  var workflowStateRef = opts.workflowStateRef;

  var [connectionDrag, setConnectionDrag] = useState(null);
  var [connectionTargetNodeId, setConnectionTargetNodeId] = useState(null);
  var [propPickerState, setPropPickerState] = useState(null);
  var connectionTargetIdRef = useRef(null);
  var bendDragRef = useRef(null);

  // Connection drag start
  function startConnectionDrag(nodeId, fromAnchor, event) {
    event.preventDefault();
    event.stopPropagation();
    if (!canvasRef.current) return;
    var rect = canvasRef.current.getBoundingClientRect();
    var z = zoomRef.current;
    setConnectionDrag({
      mode: 'new',
      from: nodeId,
      fromAnchor: fromAnchor,
      startX: (event.clientX - rect.left - panPosRef.current.x) / z,
      startY: (event.clientY - rect.top - panPosRef.current.y) / z,
      x: (event.clientX - rect.left - panPosRef.current.x) / z,
      y: (event.clientY - rect.top - panPosRef.current.y) / z
    });
    connectionTargetIdRef.current = null;
    setConnectionTargetNodeId(null);
  }

  function startReconnectDrag(connection, event) {
    event.preventDefault();
    event.stopPropagation();
    if (!canvasRef.current) return;
    var rect = canvasRef.current.getBoundingClientRect();
    var z = zoomRef.current;
    setConnectionDrag({
      mode: 'reconnect',
      connectionId: connection.id,
      from: connection.fromId,
      fromAnchor: connection.fromAnchor,
      startX: (event.clientX - rect.left - panPosRef.current.x) / z,
      startY: (event.clientY - rect.top - panPosRef.current.y) / z,
      x: (event.clientX - rect.left - panPosRef.current.x) / z,
      y: (event.clientY - rect.top - panPosRef.current.y) / z
    });
    connectionTargetIdRef.current = null;
    setConnectionTargetNodeId(null);
  }

  function startConnectionDragTouch(nodeId, fromAnchor, event) {
    event.preventDefault();
    event.stopPropagation();
    if (!canvasRef.current || !event.touches.length) return;
    var touch = event.touches[0];
    var rect = canvasRef.current.getBoundingClientRect();
    var z = zoomRef.current;
    setConnectionDrag({
      mode: 'new',
      from: nodeId,
      fromAnchor: fromAnchor,
      startX: (touch.clientX - rect.left - panPosRef.current.x) / z,
      startY: (touch.clientY - rect.top - panPosRef.current.y) / z,
      x: (touch.clientX - rect.left - panPosRef.current.x) / z,
      y: (touch.clientY - rect.top - panPosRef.current.y) / z
    });
    connectionTargetIdRef.current = null;
    setConnectionTargetNodeId(null);
  }

  function startReconnectDragTouch(connection, event) {
    event.preventDefault();
    event.stopPropagation();
    if (!canvasRef.current || !event.touches.length) return;
    var touch = event.touches[0];
    var rect = canvasRef.current.getBoundingClientRect();
    var z = zoomRef.current;
    setConnectionDrag({
      mode: 'reconnect',
      connectionId: connection.id,
      from: connection.fromId,
      fromAnchor: connection.fromAnchor,
      startX: (touch.clientX - rect.left - panPosRef.current.x) / z,
      startY: (touch.clientY - rect.top - panPosRef.current.y) / z,
      x: (touch.clientX - rect.left - panPosRef.current.x) / z,
      y: (touch.clientY - rect.top - panPosRef.current.y) / z
    });
    connectionTargetIdRef.current = null;
    setConnectionTargetNodeId(null);
  }

  // Connection CRUD
  function deleteConnection(connectionId, event) {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    setWorkflowState(function(prev) {
      return Object.assign({}, prev, {
        connections: (prev.connections || []).filter(function(c) { return c.id !== connectionId; })
      });
    });
  }

  function toggleConnectionNoWait(connectionId, event) {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    setWorkflowState(function(prev) {
      return Object.assign({}, prev, {
        connections: (prev.connections || []).map(function(conn) {
          if (conn.id !== connectionId) return conn;
          return Object.assign({}, conn, { noWait: !conn.noWait });
        })
      });
    });
  }

  function toggleConnectionNoData(connectionId, event) {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    setWorkflowState(function(prev) {
      var targetConn = (prev.connections || []).find(function(c) { return c.id === connectionId; });
      var newNoData = targetConn ? !targetConn.noData : false;
      var updatedConnections = (prev.connections || []).map(function(conn) {
        if (conn.id !== connectionId) return conn;
        return Object.assign({}, conn, { noData: newNoData });
      });
      var updatedNodes = (prev.nodes || []).map(function(n) {
        if (!targetConn || n.id !== targetConn.to) return n;
        if (n.templateId !== 'sys-set-variable') return n;
        return Object.assign({}, n, {
          paramValues: Object.assign({}, n.paramValues || {}, { source: newNoData ? 'false' : 'true' })
        });
      });
      return Object.assign({}, prev, { connections: updatedConnections, nodes: updatedNodes });
    });
  }

  // Bend points
  function addConnectionBend(connectionId, point) {
    if (!connectionId || !point) return;
    setWorkflowState(function(prev) {
      return Object.assign({}, prev, {
        connections: (prev.connections || []).map(function(conn) {
          if (conn.id !== connectionId) return conn;
          var bendPoints = Array.isArray(conn.bendPoints) ? conn.bendPoints.slice() : [];
          bendPoints.push({ x: Math.round(point.x), y: Math.round(point.y) });
          return Object.assign({}, conn, { bendPoints: bendPoints });
        })
      });
    });
  }

  function toggleConnectionBendAtPoint(connectionId, point) {
    if (!connectionId || !point) return;
    var removeRadiusSq = 34 * 34;
    setWorkflowState(function(prev) {
      return Object.assign({}, prev, {
        connections: (prev.connections || []).map(function(conn) {
          if (conn.id !== connectionId) return conn;
          var bendPoints = Array.isArray(conn.bendPoints) ? conn.bendPoints.slice() : [];
          var nearestIndex = -1;
          var nearestDistSq = Number.POSITIVE_INFINITY;
          for (var i = 0; i < bendPoints.length; i += 1) {
            var bp = bendPoints[i];
            var dx = Number(bp.x) - point.x;
            var dy = Number(bp.y) - point.y;
            var distSq = dx * dx + dy * dy;
            if (distSq < nearestDistSq) { nearestDistSq = distSq; nearestIndex = i; }
          }
          if (nearestIndex >= 0 && nearestDistSq <= removeRadiusSq) {
            bendPoints.splice(nearestIndex, 1);
          } else {
            bendPoints.push({ x: Math.round(point.x), y: Math.round(point.y) });
          }
          return Object.assign({}, conn, { bendPoints: bendPoints });
        })
      });
    });
  }

  function removeConnectionBend(connectionId, bendIndex, event) {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    setWorkflowState(function(prev) {
      return Object.assign({}, prev, {
        connections: (prev.connections || []).map(function(conn) {
          if (conn.id !== connectionId) return conn;
          var bendPoints = Array.isArray(conn.bendPoints) ? conn.bendPoints.slice() : [];
          if (bendIndex < 0 || bendIndex >= bendPoints.length) return conn;
          bendPoints.splice(bendIndex, 1);
          return Object.assign({}, conn, { bendPoints: bendPoints });
        })
      });
    });
  }

  // Bend drag
  function startBendDrag(connectionId, bendIndex, event) {
    event.preventDefault();
    event.stopPropagation();
    if (!canvasRef.current) return;
    bendDragRef.current = { connectionId: connectionId, bendIndex: bendIndex };
    window.addEventListener('mousemove', onBendDragMove);
    window.addEventListener('mouseup', stopBendDrag);
  }

  function onBendDragMove(event) {
    var drag = bendDragRef.current;
    if (!drag || !canvasRef.current) return;
    var rect = canvasRef.current.getBoundingClientRect();
    var z = zoomRef.current;
    var point = {
      x: (event.clientX - rect.left - panPosRef.current.x) / z,
      y: (event.clientY - rect.top - panPosRef.current.y) / z
    };
    setWorkflowState(function(prev) {
      return Object.assign({}, prev, {
        connections: (prev.connections || []).map(function(conn) {
          if (conn.id !== drag.connectionId) return conn;
          var bendPoints = Array.isArray(conn.bendPoints) ? conn.bendPoints.slice() : [];
          if (drag.bendIndex < 0 || drag.bendIndex >= bendPoints.length) return conn;
          bendPoints[drag.bendIndex] = { x: Math.round(point.x), y: Math.round(point.y) };
          return Object.assign({}, conn, { bendPoints: bendPoints });
        })
      });
    });
  }

  function stopBendDrag() {
    bendDragRef.current = null;
    window.removeEventListener('mousemove', onBendDragMove);
    window.removeEventListener('mouseup', stopBendDrag);
  }

  function onBendDragTouchMove(event) {
    var drag = bendDragRef.current;
    if (!drag || !canvasRef.current || !event.touches.length) return;
    event.preventDefault();
    var touch = event.touches[0];
    var rect = canvasRef.current.getBoundingClientRect();
    var z = zoomRef.current;
    var point = {
      x: (touch.clientX - rect.left - panPosRef.current.x) / z,
      y: (touch.clientY - rect.top - panPosRef.current.y) / z
    };
    setWorkflowState(function(prev) {
      return Object.assign({}, prev, {
        connections: (prev.connections || []).map(function(conn) {
          if (conn.id !== drag.connectionId) return conn;
          var bendPoints = Array.isArray(conn.bendPoints) ? conn.bendPoints.slice() : [];
          if (drag.bendIndex < 0 || drag.bendIndex >= bendPoints.length) return conn;
          bendPoints[drag.bendIndex] = { x: Math.round(point.x), y: Math.round(point.y) };
          return Object.assign({}, conn, { bendPoints: bendPoints });
        })
      });
    });
  }

  function stopBendDragTouch() {
    bendDragRef.current = null;
    window.removeEventListener('touchmove', onBendDragTouchMove);
    window.removeEventListener('touchend', stopBendDragTouch);
  }

  function startBendDragTouch(connectionId, bendIndex, event) {
    event.preventDefault();
    event.stopPropagation();
    if (!canvasRef.current || !event.touches.length) return;
    bendDragRef.current = { connectionId: connectionId, bendIndex: bendIndex };
    window.addEventListener('touchmove', onBendDragTouchMove, { passive: false });
    window.addEventListener('touchend', stopBendDragTouch);
  }

  // Prop picker
  function handlePropPickerSelect(propPath) {
    if (!propPickerState) return;
    var connectionId = propPickerState.connectionId;
    var effectivePath = (propPath && propPath.length > 0) ? propPath : null;
    setWorkflowState(function(prev) {
      var nextConns = (prev.connections || []).map(function(conn) {
        if (conn.id !== connectionId) return conn;
        var updated = Object.assign({}, conn);
        if (effectivePath) { updated.propPath = effectivePath; }
        else { delete updated.propPath; }
        return updated;
      });
      return Object.assign({}, prev, { connections: nextConns });
    });
    setPropPickerState(null);
  }

  function closePropPicker() {
    setPropPickerState(null);
  }

  // Cleanup
  useEffect(function() {
    return function() {
      window.removeEventListener('mousemove', onBendDragMove);
      window.removeEventListener('mouseup', stopBendDrag);
      window.removeEventListener('touchmove', onBendDragTouchMove);
      window.removeEventListener('touchend', stopBendDragTouch);
    };
  }, []);

  return {
    connectionDrag: connectionDrag,
    setConnectionDrag: setConnectionDrag,
    connectionTargetNodeId: connectionTargetNodeId,
    setConnectionTargetNodeId: setConnectionTargetNodeId,
    connectionTargetIdRef: connectionTargetIdRef,
    propPickerState: propPickerState,
    setPropPickerState: setPropPickerState,
    startConnectionDrag: startConnectionDrag,
    startReconnectDrag: startReconnectDrag,
    startConnectionDragTouch: startConnectionDragTouch,
    startReconnectDragTouch: startReconnectDragTouch,
    deleteConnection: deleteConnection,
    toggleConnectionNoWait: toggleConnectionNoWait,
    toggleConnectionNoData: toggleConnectionNoData,
    addConnectionBend: addConnectionBend,
    toggleConnectionBendAtPoint: toggleConnectionBendAtPoint,
    removeConnectionBend: removeConnectionBend,
    startBendDrag: startBendDrag,
    startBendDragTouch: startBendDragTouch,
    handlePropPickerSelect: handlePropPickerSelect,
    closePropPicker: closePropPicker
  };
}

module.exports = useConnectionManager;
